import React, { useState } from "react";
import { Tooltip } from "react-tooltip";
import DOMPurify from "dompurify";
import { usePxToVw } from "../../../hooks/usePxToVw";

const Button = ({ text, settings, keyid = 0, wayfinder = false, wayfinderClickSound = "", clickSoundVolume = 1 }) => {
  console.log("button settings", settings, text);

  console.log("Click SOund volume", wayfinder, wayfinderClickSound, clickSoundVolume);

  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  
  // Ref to store Audio instance for cleanup
  const audioRef = React.useRef(null);

  const { pxToVw } = usePxToVw();

  // Helper function to safely parse shadow color
  const getShadowColor = () => {
    const shadowColor = settings?.shadow?.color;
    if (
      !shadowColor ||
      typeof shadowColor !== "string" ||
      shadowColor.length < 7
    ) {
      return "rgba(0, 0, 0, 0.5)"; // Default shadow color
    }

    try {
      const r = parseInt(shadowColor.slice(1, 3), 16);
      const g = parseInt(shadowColor.slice(3, 5), 16);
      const b = parseInt(shadowColor.slice(5, 7), 16);
      const alpha = settings?.shadow?.alpha ?? 0.5;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } catch (error) {
      return "rgba(0, 0, 0, 0.5)"; // Fallback on error
    }
  };

  const angleInRadians = ((settings?.shadow?.angle ?? 0) * Math.PI) / 180;
  const offsetX = Math.cos(angleInRadians) * (settings?.shadow?.distance ?? 0);
  const offsetY = Math.sin(angleInRadians) * (settings?.shadow?.distance ?? 0);

  const style = {
    // Dimensions and layout
    borderRadius: pxToVw(settings?.cornerRadius),
    height: pxToVw(settings?.height),
    width: pxToVw(settings?.width),
    textAlign: settings?.textAlign,
    justifyContent: settings?.textAlign,
    marginBottom: pxToVw(settings?.marginBottom),

    // Borders
    borderColor: isHovered
      ? settings?.highlight?.borderColor
      : settings?.outlineColor,
    borderWidth: settings?.outlineThickness + "px",
    borderStyle: "solid",

    // Background and colors
    backgroundColor: isHovered
      ? settings?.highlight?.fillColor
      : settings?.fillColor,
    color: isHovered ? settings?.highlight?.fontColor : settings?.fontColor,
    fontFamily: settings?.font,
    fontSize: pxToVw(settings?.fontSize),

    // Box shadow
    boxShadow: `${offsetX}px ${offsetY}px ${settings?.shadow?.size || 0}px ${getShadowColor()}`,

    // Transition
    transition: "0.2s ease-in-out",

    cursor: "pointer",

    lineHeight: `${settings?.lineHeight}`,
    padding: `${pxToVw(settings?.padding)}`,
    display: "flex",
    alignItems: "center",

  };

  const playSound = () => {
    const audioUrl = wayfinder ? wayfinderClickSound : settings?.clickSound;
    const volume = wayfinder ? clickSoundVolume : settings?.clickSoundVolume || 1;

    console.log("clicked in function", isClicked);

    if (isClicked) return;

    if (audioUrl) {
      // Clean up previous audio if exists
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }

      const audio = new Audio(audioUrl);
      audio.volume = volume || 1;
      audioRef.current = audio;

      console.log("audioUrl in volume", audioUrl, volume)

      setIsClicked(true);

      audio.play()
        .then(() => {
          // Reset after audio plays
          audio.onended = () => {
            setIsClicked(false);
            audioRef.current = null;
          };
        })
        .catch((error) => {
          console.error("Audio playback failed:", error);
          setIsClicked(false);
          audioRef.current = null;
        });
    }
  };
  
  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const style2 = {
    backgroundImage: `url(${settings?.btnPng})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: `${pxToVw(settings?.height)}`,
    width: `${pxToVw(settings?.width)}`,
    marginBottom: pxToVw(settings?.marginBottom),
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    color: settings?.fontColor,
    fontFamily: settings?.font,
    fontSize: pxToVw(settings?.fontSize),
    borderRadius: "unset",
  };

  // Style3 for icon buttons — no backgroundImage, icon rendered as <img> instead
  const style3 = {
    borderRadius: pxToVw(settings?.cornerRadius),
    height: pxToVw(settings?.height),
    width: pxToVw(settings?.width),
    textAlign: settings?.textAlign,
    marginBottom: pxToVw(settings?.marginBottom),

    // Borders
    borderColor: isHovered
      ? settings?.highlight?.borderColor
      : settings?.outlineColor,
    borderWidth: settings?.outlineThickness + "px",
    borderStyle: "solid",

    // Background and colors
    backgroundColor: isHovered
      ? settings?.highlight?.fillColor
      : settings?.fillColor,
    color: isHovered ? settings?.highlight?.fontColor : settings?.fontColor,
    fontFamily: settings?.font,
    fontSize: pxToVw(settings?.fontSize),

    // Box shadow
    boxShadow: `${offsetX}px ${offsetY}px ${settings?.shadow?.size || 0}px ${getShadowColor()}`,

    transition: "0.2s ease-in-out",
    cursor: "pointer",
    lineHeight: `${settings?.lineHeight}`,
    padding: `${pxToVw(settings?.padding)}`,
    display: "flex",
    // Dynamic flex direction based on icon position
    flexDirection: (settings?.iconPosition === 'top' || settings?.iconPosition === 'bottom') ? 'column' : 'row',
    alignItems: "center",
    // When text is hidden, adjust justifyContent based on icon position
    justifyContent: settings?.hideBtnText 
      ? (settings?.iconPosition === 'top' ? 'flex-start' : 
         settings?.iconPosition === 'bottom' ? 'flex-end' : 
         settings?.iconPosition === 'right' ? 'flex-end' : 'flex-start')
      : (settings?.iconPosition === 'right' ? 'flex-end' : 
         settings?.iconPosition === 'left' ? 'flex-start' : 'center'),
    gap: settings?.hideBtnText ? "0" : pxToVw(settings?.iconSpacing || 8),
  };

  // Icon size as a percentage of button height — controlled via settings?.iconSize (default 50%)
  const iconSizePercent = settings?.iconSize ?? 50;
  
  // For left/right positions, limit icon width to prevent it from taking full button width
  const isHorizontalLayout = settings?.iconPosition === 'left' || settings?.iconPosition === 'right';
  const maxIconWidth = isHorizontalLayout ? '80%' : '100%'; // Limit to 80% for horizontal layouts
  
  const iconStyle = {
    height: `${iconSizePercent}%`,
    maxWidth: maxIconWidth, // Prevent icon from taking full width in horizontal layouts
    objectFit: "contain",
    flexShrink: 0,
    pointerEvents: "none",
    // Order for icon based on position
    order: (settings?.iconPosition === 'right' || settings?.iconPosition === 'bottom') ? 2 : 1,
  };

  const textStyle = { 
    // Order for text based on icon position
    order: (settings?.iconPosition === 'right' || settings?.iconPosition === 'bottom') ? 1 : 2,
  };

  // Determine which style to use
  const getButtonStyle = () => {
    if (settings?.pngBtnenabled) {
      return style2;
    } else if (settings?.enableIcon && settings?.buttonIcon) {
      return style3;
    } else {
      return style;
    }
  };

  const getButtonClass = () => {
    if (settings?.pngBtnenabled) {
      return "png-btn";
    } else if (settings?.enableIcon && settings?.buttonIcon) {
      return "icon-btn";
    } else {
      return "";
    }
  };

  const isIconButton = settings?.enableIcon && settings?.buttonIcon && !settings?.pngBtnenabled;

  return (
    <>
      <button
        type="button"
        style={getButtonStyle()}
        className={getButtonClass()}
        onClick={playSound}
        onMouseOver={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={isClicked}
        data-tooltip-id={`tooltip-${keyid}`}
        data-tooltip-content={text.length > 15 ? text : ""}
      >
        {/* Icon rendered as <img> — always visible, independent of hideBtnText */}
        {isIconButton && (
          <img
            src={settings.buttonIcon}
            alt="button-icon"
            style={iconStyle}
          />
        )}

        {/* Text — hidden when hideBtnText is true, but icon above remains visible */}
        {!settings?.hideBtnText && (
          <div
            style={textStyle}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
      </button>
    </>
  );
};

export default Button;